import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, RefreshCw, Sparkles, AlertCircle, Wand2, LayoutTemplate, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { streamGenerate } from "@/lib/streamGenerate";
import { useAuth } from "@/context/AuthContext";
import { ALL_PLATFORMS, PLATFORM_META } from "@/lib/platforms";
import { copyWithToast } from "@/lib/clipboard";
import { suggestBestTemplate } from "@/lib/templateSuggest";
import { trackEvent } from "@/lib/analytics";
import { pickNextSample } from "@/lib/sampleContent";

const SAMPLE_CONTENT = `Most content creators are stuck in a trap: they spend hours writing one great piece, publish it once, and watch it disappear.

The best creators do the opposite. They write one core piece, then remix it into 5-10 platform-native posts. A single blog post becomes a Twitter thread, a LinkedIn essay, an Instagram carousel, an email to their list, and a short video hook.

The idea isn't to write MORE — it's to make each idea travel further. Same story, different audiences, different formats. That's how you compound your voice without burning out.

The magic is in the repurposing, not the original writing.`;

const FREE_MAX = 8000;
const PRO_MAX = 40000;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [content, setContent] = useState("");
  const [selected, setSelected] = useState(["twitter_thread", "linkedin_post"]);
  const [loading, setLoading] = useState(false);
  const [outputs, setOutputs] = useState([]); // [{format, text}]
  const [usage, setUsage] = useState(null);
  const [regenerating, setRegenerating] = useState({});
  const [brandVoices, setBrandVoices] = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [dismissedSuggestions, setDismissedSuggestions] = useState(() => new Set());
  const [limitNudgeDismissed, setLimitNudgeDismissed] = useState(() => {
    try { return sessionStorage.getItem("ps_limit_nudge_dismissed") === "1"; } catch { return false; }
  });
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  // Studio activation state (Phases 1-5)
  const [stats, setStats] = useState({ total_generations: 0, viewed_pricing_ever: false });
  const [sampleSeen, setSampleSeen] = useState(() => new Set());
  const [lastGenElapsedMs, setLastGenElapsedMs] = useState(null);
  const [showSecondGenNudge, setShowSecondGenNudge] = useState(false);
  const [proNudgeDismissed, setProNudgeDismissed] = useState(() => {
    try { return sessionStorage.getItem("ps_pro_nudge_dismissed") === "1"; } catch { return false; }
  });
  const reuseAppliedRef = useRef(false);
  const [firstGenTutorial, setFirstGenTutorial] = useState(false);
  const [showNextSteps, setShowNextSteps] = useState(false);
  const autoRunPendingRef = useRef(false);
  const autoRunFiredRef = useRef(false);

  const maxChars = user?.plan === "pro" ? PRO_MAX : FREE_MAX;
  const chars = content.length;
  const words = useMemo(() => (content.trim() ? content.trim().split(/\s+/).length : 0), [content]);
  const overLimit = chars > maxChars;

  const fetchUsage = async () => {
    try {
      const { data } = await api.get("/usage");
      setUsage(data);
    } catch { /* ignore */ }
  };
  const fetchBrandVoice = async () => {
    try {
      const { data } = await api.get("/brand-voices");
      const items = data.items || [];
      setBrandVoices(items);
      // Auto-select the most recent voice (already sorted by updated_at desc on server)
      if (items.length > 0 && !selectedVoiceId) {
        setSelectedVoiceId(items[0].id);
      }
    } catch { /* ignore */ }
  };
  const fetchTemplates = async () => {
    try {
      const { data } = await api.get("/templates");
      setTemplates(data.items || []);
    } catch { /* ignore */ }
  };
  const fetchStats = async () => {
    try {
      const { data } = await api.get("/stats/me");
      setStats(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchUsage(); fetchBrandVoice(); fetchTemplates(); fetchStats(); }, []);

  // Apply "Repurpose Again" state from history navigation
  useEffect(() => {
    if (reuseAppliedRef.current) return;
    const st = location.state;
    if (st?.onboarding_first_gen) {
      reuseAppliedRef.current = true;
      setFirstGenTutorial(true);
      setContent(SAMPLE_CONTENT);
      setSelected(["twitter_thread", "linkedin_post"]);
      if (st.auto_run) autoRunPendingRef.current = true;
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    if (st?.reuse) {
      reuseAppliedRef.current = true;
      // Track repurpose usage (source is History or Ideas depending on origin)
      trackEvent("repurpose_used", {
        source: st.reuse.source || "history",
        has_brand_voice: !!st.reuse.brand_voice_id,
        formats: Array.isArray(st.reuse.formats) ? st.reuse.formats.length : 0,
      });
      if (st.reuse.content) setContent(st.reuse.content);
      if (Array.isArray(st.reuse.formats) && st.reuse.formats.length > 0) {
        setSelected(st.reuse.formats);
      }
      if (st.reuse.brand_voice_id) {
        setSelectedVoiceId(st.reuse.brand_voice_id);
      }
      if (Array.isArray(st.outputs) && st.outputs.length > 0) {
        setOutputs(st.outputs);
        if (st.reuse.brand_voice_name) {
          toast.success(`Repurposed · Voice: ${st.reuse.brand_voice_name}`);
        } else {
          toast.success("Repurposed to Studio");
        }
      } else if (st.reuse.brand_voice_name) {
        toast.success(`Loaded to Studio · Voice: ${st.reuse.brand_voice_name}`);
      } else {
        toast.success("Loaded to Studio — pick formats and hit Generate");
      }
      // Clear navigation state so refresh doesn't re-apply
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const toggleFormat = (key) => {
    setSelected((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  };

  const activeTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  // Heuristic template suggestion — recomputed on every keystroke (cheap).
  // Hidden if: user already picked a template, no confident pick, or the
  // top pick was dismissed in this session.
  const templateSuggestion = useMemo(() => {
    if (selectedTemplateId) return null;
    if (templates.length === 0) return null;
    const pick = suggestBestTemplate(content, templates);
    if (!pick) return null;
    if (dismissedSuggestions.has(pick.template.id)) return null;
    return pick;
  }, [content, templates, selectedTemplateId, dismissedSuggestions]);

  const applyTemplateSuggestion = () => {
    if (!templateSuggestion) return;
    handleTemplateChange(templateSuggestion.template.id);
    toast.success(`Applied template · ${templateSuggestion.template.name}`);
  };

  const dismissTemplateSuggestion = () => {
    if (!templateSuggestion) return;
    setDismissedSuggestions((cur) => {
      const next = new Set(cur);
      next.add(templateSuggestion.template.id);
      return next;
    });
  };

  const loadSample = () => {
    const sample = pickNextSample(sampleSeen);
    setContent(sample.content);
    // Ensure the two most-used formats are pre-selected so the sample runs
    // through what users actually want to see (Phase 7 positioning).
    setSelected((cur) => {
      const wanted = ["twitter_thread", "linkedin_post"];
      const next = [...cur];
      wanted.forEach((f) => { if (!next.includes(f)) next.push(f); });
      return next;
    });
    setSampleSeen((cur) => new Set([...cur, sample.id]));
    // Move focus to the textarea so the user's next action is obvious
    setTimeout(() => {
      const ta = document.querySelector('textarea[data-testid="content-textarea"]');
      if (ta) ta.focus();
    }, 60);
    toast.success(`Sample loaded · ${sample.label}. Hit Generate to see the remix.`);
  };

  const clearAndReset = () => {
    setContent("");
    setOutputs([]);
    setShowSecondGenNudge(false);
    setLastGenElapsedMs(null);
    setTimeout(() => {
      const ta = document.querySelector('textarea[data-testid="content-textarea"]');
      if (ta) ta.focus();
    }, 60);
  };

  const handleTemplateChange = (id) => {
    setSelectedTemplateId(id);
    if (!id) return;
    const tpl = templates.find((t) => t.id === id);
    if (tpl && !selected.includes(tpl.target_format)) {
      setSelected((cur) => [...cur, tpl.target_format]);
    }
  };

  const templateApplicable =
    !!activeTemplate && selected.includes(activeTemplate.target_format);

  const canGenerate = content.trim().length > 20 && selected.length > 0 && !overLimit && !loading;

  // Auto-fire the first-run demo generation once state is ready.
  // Guarded by a ref so it fires exactly once per mount.
  useEffect(() => {
    if (!autoRunPendingRef.current) return;
    if (autoRunFiredRef.current) return;
    if (!canGenerate) return;
    autoRunFiredRef.current = true;
    autoRunPendingRef.current = false;
    handleGenerate();
  }, [canGenerate]);

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    const genStartedAt = Date.now();
    // Seed empty output cards so they render skeletons immediately
    setOutputs(selected.map((f) => ({ format: f, text: "" })));
    const useVoice = !!selectedVoiceId;
    let finalUsage = null;
    let voiceName = null;
    let voiceApplied = false;
    let templateNameApplied = null;
    try {
      await streamGenerate({
        content,
        formats: selected,
        use_brand_voice: useVoice,
        brand_voice_id: useVoice ? selectedVoiceId : null,
        template_id: selectedTemplateId || null,
        onEvent: (evt) => {
          if (evt.type === "meta") {
            voiceApplied = !!evt.brand_voice_applied;
            voiceName = evt.brand_voice_name;
            if (evt.template_applied) templateNameApplied = evt.template_name;
          } else if (evt.type === "delta") {
            setOutputs((prev) =>
              prev.map((o) => (o.format === evt.format ? { ...o, text: (o.text || "") + evt.text } : o))
            );
          } else if (evt.type === "format_end") {
            // replace with cleaned final text
            setOutputs((prev) =>
              prev.map((o) => (o.format === evt.format ? { ...o, text: evt.text } : o))
            );
          } else if (evt.type === "done") {
            setOutputs(evt.generation.outputs);
            finalUsage = evt.usage;
            voiceApplied = !!evt.brand_voice_applied;
            voiceName = evt.brand_voice_name;
            if (evt.template_applied) templateNameApplied = evt.template_name;
          } else if (evt.type === "error") {
            throw new Error(evt.detail || "Generation failed");
          } else if (evt.type === "format_error") {
            toast.error(`${evt.format} failed: ${evt.detail}`);
          }
        },
      });
      if (finalUsage) setUsage(finalUsage);
      setLastGenElapsedMs(Date.now() - genStartedAt);
      const parts = [];
      if (voiceApplied) parts.push(`Voice: ${voiceName}`);
      if (templateNameApplied) parts.push(`Template: ${templateNameApplied}`);
      toast.success(parts.length ? `Remix ready · ${parts.join(" · ")}` : "Remix ready");
      // Refresh stats + trigger second-gen nudge for users who just crossed 1 gen
      const prevTotal = stats.total_generations;
      fetchStats();
      if (prevTotal === 0) {
        setShowSecondGenNudge(true);
      } else {
        setShowSecondGenNudge(false);
      }
      if (firstGenTutorial) {
        setShowNextSteps(true);
        setFirstGenTutorial(false);
      }
    } catch (err) {
      const status = err?.status;
      const detail = err?.detail || err?.message || "Generation failed";
      if (status === 402) {
        setLimitModalOpen(true);
      } else {
        toast.error(detail);
      }
      setOutputs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async (fmt) => {
    setRegenerating((r) => ({ ...r, [fmt]: true }));
    // Blank the target card so it visibly restreams
    setOutputs((prev) => prev.map((o) => (o.format === fmt ? { ...o, text: "" } : o)));
    const useVoice = !!selectedVoiceId;
    let finalUsage = null;
    let voiceName = null;
    let voiceApplied = false;
    try {
      await streamGenerate({
        content,
        formats: [fmt],
        use_brand_voice: useVoice,
        brand_voice_id: useVoice ? selectedVoiceId : null,
        template_id: selectedTemplateId && activeTemplate?.target_format === fmt ? selectedTemplateId : null,
        onEvent: (evt) => {
          if (evt.type === "meta") {
            voiceApplied = !!evt.brand_voice_applied;
            voiceName = evt.brand_voice_name;
          } else if (evt.type === "delta") {
            setOutputs((prev) =>
              prev.map((o) => (o.format === fmt ? { ...o, text: (o.text || "") + evt.text } : o))
            );
          } else if (evt.type === "format_end") {
            setOutputs((prev) =>
              prev.map((o) => (o.format === fmt ? { ...o, text: evt.text } : o))
            );
          } else if (evt.type === "done") {
            setOutputs((prev) =>
              prev.map((o) => (o.format === fmt ? evt.generation.outputs[0] : o))
            );
            finalUsage = evt.usage;
            voiceApplied = !!evt.brand_voice_applied;
            voiceName = evt.brand_voice_name;
          } else if (evt.type === "error") {
            throw new Error(evt.detail || "Regenerate failed");
          }
        },
      });
      if (finalUsage) setUsage(finalUsage);
      toast.success(
        voiceApplied
          ? `${PLATFORM_META[fmt].short} regenerated · ${voiceName}`
          : `${PLATFORM_META[fmt].short} regenerated`
      );
    } catch (err) {
      const status = err?.status;
      const detail = err?.detail || err?.message || "Regenerate failed";
      if (status === 402) {
        setLimitModalOpen(true);
      } else {
        toast.error(detail);
      }
    } finally {
      setRegenerating((r) => ({ ...r, [fmt]: false }));
    }
  };

  const handleCopy = async (text, fmt) => {
    await copyWithToast(text, PLATFORM_META[fmt].short);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-12 lg:px-10">
        {firstGenTutorial && (
          <div
            data-testid="onboarding-tutorial-banner"
            className="mb-8 flex items-start gap-4 border-l-4 border-black bg-white p-5"
          >
            <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="font-serif text-lg font-semibold">
                {loading ? "Your first remix is running now." : "Your first remix is ready to run."}
              </p>
              <p className="mt-1 text-sm text-neutral-600">
                {loading
                  ? "We've loaded a sample piece and started generating a Twitter thread and a LinkedIn post in parallel. Watch the cards below fill in live."
                  : "We've loaded a sample piece and picked two formats for you. Hit Generate below to see it live."}
              </p>
            </div>
          </div>
        )}
        {showNextSteps && (
          <div
            data-testid="onboarding-next-steps"
            className="mb-8 border border-neutral-200 bg-white p-6"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="mb-2 text-xs uppercase tracking-[0.25em] text-neutral-500">— Nicely done</p>
                <h2 className="font-serif text-2xl font-bold tracking-tight">You just made your first remix.</h2>
                <p className="mt-2 text-sm text-neutral-600">Here are three things you can try next:</p>
              </div>
              <button
                type="button"
                onClick={() => setShowNextSteps(false)}
                data-testid="onboarding-next-steps-dismiss"
                className="p-1 text-neutral-500 hover:text-black"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-neutral-700">
              <li>
                <span className="mr-2 text-neutral-400">01.</span>
                <strong>Set your Brand Voice.</strong> Paste 2 samples of your writing on{" "}
                <Link to="/brand-voice" className="underline underline-offset-4">Voice</Link>{" "}
                so every remix sounds like you.
              </li>
              <li>
                <span className="mr-2 text-neutral-400">02.</span>
                <strong>Try a Template.</strong> Enforce a specific structure like{" "}
                <em>Bold Hook + 3 Points + CTA</em> from{" "}
                <Link to="/templates" className="underline underline-offset-4">Templates</Link>.
              </li>
              <li>
                <span className="mr-2 text-neutral-400">03.</span>
                <strong>Never run dry.</strong> Head to{" "}
                <Link to="/ideas" className="underline underline-offset-4">Ideas</Link> and
                generate 10 fresh angles to remix.
              </li>
              <li>
                <span className="mr-2 text-neutral-400">04.</span>
                <strong>Save what you love.</strong> Star favorites in{" "}
                <Link to="/history" className="underline underline-offset-4">History</Link>{" "}
                so you can reuse or spin new versions later.
              </li>
            </ul>
          </div>
        )}
        {/* Header */}
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 text-xs uppercase tracking-[0.25em] text-neutral-500">— The studio</p>
            <h1 className="font-serif text-4xl font-bold tracking-tight sm:text-5xl">
              Paste. Pick. Remix.
            </h1>
            <p
              data-testid="dashboard-tagline"
              className="mt-3 max-w-xl text-sm text-neutral-600"
            >
              Turn any content into X threads, LinkedIn posts, and more.
            </p>
          </div>
          {usage && (
            <div
              data-testid="usage-badge"
              className={`rounded-none border px-4 py-3 text-xs ${
                usage.plan === "pro"
                  ? "border-coral bg-coral-subtle"
                  : usage.remaining <= 1
                  ? "border-coral bg-white"
                  : "border-neutral-200 bg-white"
              }`}
            >
              <span className={`uppercase tracking-widest ${usage.plan === "pro" ? "text-coral" : "text-neutral-500"}`}>
                {usage.plan === "pro" ? "Pro · unlimited" : "Free plan"}
              </span>
              <div className="mt-1 font-serif text-lg font-semibold">
                {usage.plan === "pro" ? (
                  <span data-testid="usage-remaining">Unlimited</span>
                ) : (
                  <span>
                    <span data-testid="usage-remaining" className={usage.remaining <= 1 ? "text-coral" : ""}>
                      {usage.remaining}
                    </span>
                    <span className="text-neutral-400"> / {usage.limit} left</span>
                  </span>
                )}
              </div>
              {usage.plan !== "pro" && usage.remaining <= 2 && (
                <Link
                  to="/pricing"
                  data-testid="usage-upgrade-link"
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-widest text-coral hover:text-coral-hover"
                >
                  Upgrade to Pro →
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Free plan limit-reached nudge — the moment a free user hits 5/5 this month */}
        {usage &&
          usage.plan !== "pro" &&
          usage.remaining === 0 &&
          !limitNudgeDismissed && (
            <div
              data-testid="studio-limit-nudge"
              className="mb-8 flex flex-wrap items-center gap-4 border-2 border-coral bg-coral-subtle p-5"
            >
              <Sparkles className="h-6 w-6 flex-shrink-0 text-coral" strokeWidth={1.5} />
              <div className="flex-1 min-w-[220px]">
                <p className="text-[10px] uppercase tracking-[0.25em] text-coral">
                  You&apos;ve unlocked all 5 free remixes this month
                </p>
                <p className="mt-1 font-serif text-xl font-semibold text-neutral-900">
                  Keep going with unlimited remixes on Pro.
                </p>
                <p className="mt-1 text-xs text-neutral-600">
                  $19/mo · 7-day free trial · cancel anytime.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to="/pricing"
                  data-testid="studio-limit-nudge-upgrade"
                  className="inline-flex h-11 items-center rounded-[3px] bg-coral px-5 text-xs font-medium uppercase tracking-widest text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
                >
                  See Pro plan →
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    try { sessionStorage.setItem("ps_limit_nudge_dismissed", "1"); } catch { /* ignore */ }
                    setLimitNudgeDismissed(true);
                  }}
                  data-testid="studio-limit-nudge-dismiss"
                  className="p-2 text-neutral-500 hover:text-black"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          )}

        {/* First-use empty state: "one → many" visual + Try an Example button.
            Only shown to brand-new users (no generations yet) with an empty textarea.
            Every button here does exactly what it says (Phase 1 activation). */}
        {stats.total_generations === 0 && content.trim().length === 0 && (
          <div
            data-testid="studio-empty-state"
            className="mb-8 border border-neutral-200 bg-white p-6 sm:p-8"
          >
            <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.25em] text-coral">— New here?</p>
                <p className="mt-2 font-serif text-xl text-neutral-900 sm:text-2xl">
                  <span className="font-bold">One piece of content</span>
                  <span className="text-neutral-400"> → </span>
                  <span className="font-bold">multiple platform-ready formats.</span>
                </p>
                <p className="mt-2 text-sm text-neutral-600">
                  Load a short sample and see the transformation in about 10 seconds.
                  You can also paste your own content in the box below.
                </p>
              </div>
              <button
                type="button"
                onClick={loadSample}
                data-testid="try-example-btn"
                className="inline-flex h-12 flex-shrink-0 items-center gap-2 rounded-[3px] bg-coral px-6 text-sm font-medium uppercase tracking-widest text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
              >
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                Try an example
              </button>
            </div>
          </div>
        )}

        {/* Input textarea */}
        <div className="border border-neutral-200 bg-white">
          <Textarea
            data-testid="content-textarea"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Paste your blog post, transcript, notes, or long-form here..."
            className="min-h-[280px] resize-none rounded-none border-0 bg-transparent p-8 font-serif text-lg leading-relaxed placeholder:text-neutral-400 focus-visible:ring-0"
          />
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-100 px-8 py-4 text-xs text-neutral-500">
            <div className="flex items-center gap-4">
              <span data-testid="word-count"><span className="font-mono">{words}</span> words</span>
              <span className="text-neutral-300">·</span>
              <span data-testid="char-count" className={overLimit ? "font-mono text-coral" : "font-mono"}>
                {chars.toLocaleString()} / {maxChars.toLocaleString()} chars
              </span>
            </div>
            {overLimit && (
              <span className="flex items-center gap-1 text-coral" data-testid="over-limit-warning">
                <AlertCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
                {usage?.plan === "pro" ? "Trim to your plan's limit" : (
                  <>
                    Trim, or{" "}
                    <Link to="/pricing" className="underline underline-offset-4 hover:text-coral-hover">
                      upgrade to Pro
                    </Link>
                    {" "}for 40k chars
                  </>
                )}
              </span>
            )}
          </div>
        </div>

        {/* Format chips */}
        <div className="mt-10">
          <p className="mb-4 text-xs uppercase tracking-[0.25em] text-neutral-500">— Pick your channels</p>
          <div className="flex flex-wrap gap-3">
            {ALL_PLATFORMS.map((key) => {
              const active = selected.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`format-chip-${key}`}
                  onClick={() => toggleFormat(key)}
                  className={`rounded-full border px-5 py-2 text-sm transition-colors ${
                    active
                      ? "border-coral bg-coral-subtle text-coral"
                      : "border-neutral-300 bg-white text-neutral-700 hover:border-neutral-900 hover:text-neutral-900"
                  }`}
                >
                  {PLATFORM_META[key].label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Generate */}
        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
          <Button
            onClick={handleGenerate}
            disabled={!canGenerate}
            data-testid="generate-btn"
            size="lg"
            className={`h-14 w-full min-w-0 rounded-[3px] bg-coral px-10 text-base text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral disabled:opacity-50 sm:h-12 sm:w-auto sm:text-sm ${
              firstGenTutorial ? "ring-4 ring-coral/25 ring-offset-2 animate-pulse" : ""
            }`}
          >
            {loading ? (
              <>
                <div className="mr-3 h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Remixing…
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Generate {selected.length > 0 && `(${selected.length})`}
              </>
            )}
          </Button>

          {/* Brand Voice picker */}
          {brandVoices.length === 0 ? (
            <Link
              to="/brand-voice"
              data-testid="brand-voice-setup-link"
              className="flex w-full items-center justify-center gap-2 border border-dashed border-neutral-300 px-4 py-3 text-sm text-neutral-600 hover:border-black hover:text-black sm:w-auto sm:py-2"
            >
              <Wand2 className="h-4 w-4" strokeWidth={1.5} />
              Set up Brand Voice →
            </Link>
          ) : (
            <div
              data-testid="brand-voice-picker-wrap"
              className={`flex w-full min-w-0 items-center gap-2 border px-3 py-3 text-sm sm:w-auto sm:py-2 ${
                selectedVoiceId ? "border-black bg-black text-white" : "border-neutral-300 bg-white text-neutral-500"
              }`}
            >
              <Wand2 className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
              <label htmlFor="voice-picker" className="whitespace-nowrap text-xs uppercase tracking-widest">
                Voice
              </label>
              <select
                id="voice-picker"
                data-testid="brand-voice-picker"
                value={selectedVoiceId}
                onChange={(e) => setSelectedVoiceId(e.target.value)}
                className={`min-w-0 flex-1 cursor-pointer truncate border-none bg-transparent pr-6 text-sm font-medium focus:outline-none focus:ring-0 sm:flex-none ${
                  selectedVoiceId ? "text-white" : "text-neutral-700"
                }`}
              >
                <option value="" className="bg-white text-neutral-900">None</option>
                {brandVoices.map((v) => (
                  <option key={v.id} value={v.id} className="bg-white text-neutral-900">
                    {v.name}
                  </option>
                ))}
              </select>
              <Link
                to="/brand-voice"
                data-testid="brand-voice-manage-link"
                className={`ml-1 flex-shrink-0 whitespace-nowrap text-xs underline underline-offset-4 ${
                  selectedVoiceId ? "text-neutral-300 hover:text-white" : "text-neutral-400 hover:text-black"
                }`}
              >
                Manage
              </Link>
            </div>
          )}

          {/* Template picker */}
          <div
            data-testid="template-picker-wrap"
            className={`flex w-full min-w-0 items-center gap-2 border px-3 py-3 text-sm sm:w-auto sm:py-2 ${
              selectedTemplateId ? "border-black bg-black text-white" : "border-neutral-300 bg-white text-neutral-500"
            }`}
          >
            <LayoutTemplate className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
            <label htmlFor="template-picker" className="whitespace-nowrap text-xs uppercase tracking-widest">
              Template
            </label>
            <select
              id="template-picker"
              data-testid="template-picker"
              value={selectedTemplateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className={`min-w-0 flex-1 cursor-pointer truncate border-none bg-transparent pr-6 text-sm font-medium focus:outline-none focus:ring-0 sm:flex-none ${
                selectedTemplateId ? "text-white" : "text-neutral-700"
              }`}
            >
              <option value="" className="bg-white text-neutral-900">None</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id} className="bg-white text-neutral-900">
                  {t.name} — {PLATFORM_META[t.target_format]?.short || t.target_format}
                </option>
              ))}
            </select>
            <Link
              to="/templates"
              data-testid="template-manage-link"
              className={`ml-1 flex-shrink-0 whitespace-nowrap text-xs underline underline-offset-4 ${
                selectedTemplateId ? "text-neutral-300 hover:text-white" : "text-neutral-400 hover:text-black"
              }`}
            >
              Manage
            </Link>
          </div>

          <span className="text-xs text-neutral-500">
            Runs all selected formats in parallel · Claude Haiku 4.5
          </span>
        </div>

        {/* Smart template suggestion — client-side heuristic */}
        {templateSuggestion && (
          <div
            data-testid="template-suggestion"
            className="mt-4 flex flex-wrap items-center gap-3 border border-coral bg-coral-subtle px-4 py-3 text-xs"
          >
            <Sparkles className="h-4 w-4 flex-shrink-0 text-coral" strokeWidth={1.5} />
            <span className="text-neutral-800">
              Suggested template:{" "}
              <span className="font-semibold" data-testid="template-suggestion-name">
                {templateSuggestion.template.name}
              </span>{" "}
              <span className="text-neutral-500">— {templateSuggestion.reason}.</span>
            </span>
            <button
              type="button"
              onClick={applyTemplateSuggestion}
              data-testid="template-suggestion-apply"
              className="ml-auto flex items-center gap-1 rounded-[3px] bg-coral px-3 py-1.5 text-[11px] font-medium uppercase tracking-widest text-white transition-colors hover:bg-coral-hover"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={dismissTemplateSuggestion}
              data-testid="template-suggestion-dismiss"
              className="p-1 text-neutral-500 hover:text-black"
              aria-label="Dismiss suggestion"
            >
              <X className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
        )}

        {/* Template active hint */}
        {activeTemplate && (
          <div
            data-testid="template-active-hint"
            className="mt-4 flex flex-wrap items-center gap-3 border-l-4 border-black bg-white px-4 py-3 text-xs"
          >
            <LayoutTemplate className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
            <span className="text-neutral-700">
              Template <span className="font-semibold">{activeTemplate.name}</span> will be applied to{" "}
              <span className="font-semibold">
                {PLATFORM_META[activeTemplate.target_format]?.label || activeTemplate.target_format}
              </span>
              .
            </span>
            {!templateApplicable && (
              <span
                data-testid="template-inactive-warning"
                className="flex items-center gap-1 text-amber-700"
              >
                <AlertCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
                {PLATFORM_META[activeTemplate.target_format]?.short || "That format"} isn&apos;t selected — the template will be skipped.
              </span>
            )}
          </div>
        )}

        {/* Outputs */}
        {(loading || outputs.length > 0) && (
          <div className="mt-16">
            <div className="mb-6 flex items-baseline justify-between">
              <p className="text-xs uppercase tracking-[0.25em] text-neutral-500">— The remix</p>
              {!loading && outputs.length > 0 && (
                <Link
                  to="/history"
                  data-testid="saved-to-history-link"
                  className="group text-xs text-neutral-500 hover:text-black"
                >
                  <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 align-middle" />
                  Saved to your history
                  <span className="ml-1 inline-block transition-transform group-hover:translate-x-1">→</span>
                </Link>
              )}
            </div>

            {/* Honest transformation summary — real counts, real elapsed time.
                Only shown after a successful generation completes. */}
            {!loading && outputs.length > 0 && lastGenElapsedMs !== null && (
              <div
                data-testid="aha-summary"
                className="mb-6 border-l-4 border-coral bg-coral-subtle/40 px-5 py-3"
              >
                <p className="font-serif text-lg text-neutral-900">
                  Your content was transformed into{" "}
                  <span className="font-bold">{outputs.length} platform-ready {outputs.length === 1 ? "format" : "formats"}</span>
                  {" "}in{" "}
                  <span className="font-bold">{(lastGenElapsedMs / 1000).toFixed(1)}s</span>.
                </p>
              </div>
            )}
            <div className="space-y-6">
              {outputs.map((out) => {
                const isRegen = !!regenerating[out.format];
                const isStreaming = (loading || isRegen) && !out.text;
                const isPartial = (loading || isRegen) && !!out.text;
                return (
                  <div
                    key={out.format}
                    data-testid={`output-card-${out.format}`}
                    className="animate-fade-in border border-neutral-200 bg-white p-8"
                  >
                    <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 pb-4">
                      <div>
                        <p className="text-xs uppercase tracking-widest text-neutral-500">
                          {PLATFORM_META[out.format].description}
                        </p>
                        <h3 className="font-serif text-2xl font-semibold tracking-tight">
                          {PLATFORM_META[out.format].label}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                        {isStreaming || isPartial ? (
                          <span className="flex items-center gap-2 text-xs text-neutral-500" data-testid={`output-streaming-${out.format}`}>
                            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-black" />
                            Streaming…
                          </span>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid={`regen-btn-${out.format}`}
                              onClick={() => handleRegenerate(out.format)}
                              disabled={regenerating[out.format]}
                              className="h-9 rounded-none border-neutral-300 text-xs"
                            >
                              <RefreshCw
                                className={`mr-2 h-3.5 w-3.5 ${regenerating[out.format] ? "animate-spin" : ""}`}
                                strokeWidth={1.5}
                              />
                              Regenerate
                            </Button>
                            <Button
                              size="sm"
                              data-testid={`copy-btn-${out.format}`}
                              onClick={() => handleCopy(out.text, out.format)}
                              className="h-9 rounded-none bg-black text-xs text-white hover:bg-neutral-800"
                            >
                              <Copy className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} />
                              Copy
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    {isStreaming ? (
                      <div className="space-y-2">
                        <div className="h-3 w-full animate-pulse bg-neutral-100" />
                        <div className="h-3 w-11/12 animate-pulse bg-neutral-100" />
                        <div className="h-3 w-9/12 animate-pulse bg-neutral-100" />
                        <div className="h-3 w-10/12 animate-pulse bg-neutral-100" />
                      </div>
                    ) : (
                      <pre className="whitespace-pre-wrap break-words font-sans text-[15px] leading-relaxed text-neutral-800">
                        {out.text}
                        {isPartial && (
                          <span className="ml-0.5 inline-block h-4 w-2 animate-pulse bg-black align-text-bottom" />
                        )}
                      </pre>
                    )}
                  </div>
                );
              })}

              {/* If no outputs yet but loading, show a placeholder card per format */}
              {loading && outputs.length === 0 &&
                selected.map((fmt) => (
                  <div key={fmt} className="border border-neutral-200 bg-white p-8">
                    <div className="mb-4 flex items-center justify-between">
                      <span className="font-serif text-xl font-semibold">{PLATFORM_META[fmt].label}</span>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-300 border-t-black" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 w-full animate-pulse bg-neutral-100" />
                      <div className="h-3 w-11/12 animate-pulse bg-neutral-100" />
                      <div className="h-3 w-9/12 animate-pulse bg-neutral-100" />
                    </div>
                  </div>
                ))}
            </div>

            {/* Post-first-gen nudges — every button below performs exactly its label */}
            {!loading && outputs.length > 0 && (
              <>
                {/* Second-generation nudge — shown only to users who just made their FIRST remix.
                    Two truthful actions: reset the studio, or load a different sample. */}
                {showSecondGenNudge && (
                  <div
                    data-testid="second-gen-nudge"
                    className="mt-10 border border-neutral-200 bg-white p-6"
                  >
                    <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">— Ready for another?</p>
                    <p className="mt-2 font-serif text-xl text-neutral-900">
                      Try another article, transcript, or idea.
                    </p>
                    <p className="mt-1 text-sm text-neutral-600">
                      The second remix is where PostShifted starts to feel useful.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={clearAndReset}
                        data-testid="second-gen-clear"
                        className="inline-flex h-11 items-center gap-2 rounded-[3px] border border-neutral-300 bg-white px-5 text-xs uppercase tracking-widest text-neutral-800 hover:border-black hover:text-black"
                      >
                        Clear and start over
                      </button>
                      <button
                        type="button"
                        onClick={loadSample}
                        data-testid="second-gen-sample"
                        className="inline-flex h-11 items-center gap-2 rounded-[3px] bg-coral px-5 text-xs uppercase tracking-widest text-white transition-all duration-300 hover:bg-coral-hover"
                      >
                        <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />
                        Try a different piece
                      </button>
                    </div>
                  </div>
                )}

                {/* Repurpose discovery — surfaces the EXISTING repurpose feature at the
                    exact moment the user is looking at results. Reuses the same
                    /history navigation flow; no new functionality is created here. */}
                <div
                  data-testid="repurpose-hint"
                  className="mt-4 flex flex-wrap items-center gap-3 border border-dashed border-neutral-300 bg-white px-5 py-3 text-sm"
                >
                  <RefreshCw className="h-4 w-4 flex-shrink-0 text-neutral-500" strokeWidth={1.5} />
                  <span className="flex-1 min-w-[220px] text-neutral-700">
                    Want to remix these into different formats or with your Brand Voice?{" "}
                    <span className="text-neutral-500">Every remix is saved to History with a one-click Repurpose action.</span>
                  </span>
                  <Link
                    to="/history"
                    data-testid="repurpose-hint-link"
                    className="text-xs uppercase tracking-widest text-coral underline underline-offset-4 hover:text-coral-hover"
                  >
                    Open History →
                  </Link>
                </div>

                {/* Value-first Pro nudge — ONLY for engaged free users (3+ gens) who haven't
                    visited Pricing yet, and who haven't dismissed this session. Non-blocking. */}
                {usage &&
                  usage.plan !== "pro" &&
                  stats.total_generations >= 3 &&
                  !stats.viewed_pricing_ever &&
                  !proNudgeDismissed && (
                  <div
                    data-testid="pro-nudge-engaged"
                    className="mt-4 flex flex-wrap items-center gap-3 border border-coral bg-coral-subtle px-5 py-3 text-sm"
                  >
                    <Sparkles className="h-4 w-4 flex-shrink-0 text-coral" strokeWidth={1.5} />
                    <span className="flex-1 min-w-[220px] text-neutral-800">
                      You&apos;ve made {stats.total_generations} remixes with PostShifted.{" "}
                      <span className="text-neutral-600">
                        Unlock unlimited monthly remixes and every Pro feature.
                      </span>
                    </span>
                    <Link
                      to="/pricing"
                      data-testid="pro-nudge-engaged-link"
                      className="text-xs uppercase tracking-widest text-coral underline underline-offset-4 hover:text-coral-hover"
                    >
                      See Pro plans →
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        try { sessionStorage.setItem("ps_pro_nudge_dismissed", "1"); } catch { /* ignore */ }
                        setProNudgeDismissed(true);
                      }}
                      data-testid="pro-nudge-engaged-dismiss"
                      className="p-1 text-neutral-500 hover:text-black"
                      aria-label="Dismiss"
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>

      {/* Post-limit upgrade modal — fires on 402 from Generate or Regenerate */}
      {limitModalOpen && (
        <div
          data-testid="limit-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
          onClick={() => setLimitModalOpen(false)}
        >
          <div
            className="w-full max-w-lg border border-neutral-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Coral header strip */}
            <div className="h-1.5 w-full bg-coral" />

            <div className="p-8">
              <div className="mb-5 flex items-center justify-between">
                <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-coral">
                  <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />
                  — You&apos;ve hit the free limit
                </p>
                <button
                  type="button"
                  onClick={() => setLimitModalOpen(false)}
                  data-testid="limit-modal-close"
                  className="text-neutral-400 hover:text-black"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" strokeWidth={1.5} />
                </button>
              </div>

              <h2 className="mb-3 font-serif text-3xl font-bold leading-tight tracking-tight text-neutral-900">
                Ready to go unlimited?
              </h2>
              <p className="mb-6 text-sm text-neutral-600">
                You&apos;ve used all 5 free remixes this month. Upgrade to Pro and remix as much as you want, for every channel — no more waiting.
              </p>

              <ul className="mb-6 space-y-2.5 border-y border-neutral-200 py-5 text-sm">
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-coral" />
                  <span><span className="font-medium">Unlimited remixes</span> — no monthly cap</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-coral" />
                  <span><span className="font-medium">All 5 channels</span> — X, LinkedIn Post, LinkedIn Carousel, Instagram, Email</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-coral" />
                  <span><span className="font-medium">Brand Voice + custom templates</span> — keep your tone consistent every time</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-coral" />
                  <span><span className="font-medium">Full history</span> — every remix saved and searchable</span>
                </li>
              </ul>

              <div className="mb-6 flex items-baseline gap-2">
                <span className="font-serif text-4xl font-bold text-neutral-900">$19</span>
                <span className="text-sm text-neutral-500">/ month · 7-day free trial · cancel anytime</span>
              </div>

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => setLimitModalOpen(false)}
                  data-testid="limit-modal-later"
                  className="h-11 px-3 text-xs uppercase tracking-widest text-neutral-500 hover:text-black"
                >
                  Maybe later
                </button>
                <Link
                  to="/pricing"
                  data-testid="limit-modal-upgrade"
                  onClick={() => setLimitModalOpen(false)}
                  className="inline-flex h-12 items-center justify-center rounded-[3px] bg-coral px-6 text-sm font-medium uppercase tracking-widest text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
                >
                  Start free trial →
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
